/*
  Early Bird season-pass card (client-approved mockup C, 2026-09-16).
  Lives in two places:
    - Ultimate Lap microsite, "2026-27 Season." - between the heading and the
      season timeline (tone="dark" surroundings, white card).
    - Grid Masters landing, "Enter Ultimate Lap." - same spot; the band is
      white so the card takes a hairline border (tone="light").
  Offer: AED 1,500 + VAT a round instead of 1,750; six rounds paid together.
  Per car. No deadline yet. All figures derive from the round fee/VAT so
  they cannot drift from the entry form.
  CTA -> the dedicated season entry flow, /events/ultimate-lap/enter/season
  (same form as a round, priced as the pass; one Stripe payment per purchase).
*/
import { Link } from "react-router-dom";
import "./early-bird.css";

export const EARLY_BIRD = { roundFee: 1750, earlyFee: 1500, rounds: 6, vatPct: 5 };
// Client, 2026-10-07: the early bird offer is OVER. Nothing is deleted - the
// card, the /enter chooser and the /enter/season flow all stay in the codebase
// behind this one switch so the offer can come back in some form later.
// false = hidden everywhere; "Sign up" goes straight to the current round.
export const EARLY_BIRD_ON = false;
export const SEASON_ENTRY_PATH = "/events/ultimate-lap/enter/season";
// Golden-hour paddock (client pick, 2026-09-17) sits above the headline.
export const EARLY_BIRD_PHOTO = "/photos/card-yas-supra-hosts.webp"; // Yas Marina, Supra #23 with the hosts (client batch, 2026-09-17)

const fmt = (n) => n.toLocaleString("en-US");

export function earlyBirdFigures(fee = EARLY_BIRD.roundFee, vat = EARLY_BIRD.vatPct) {
  const wasTotal = fee * EARLY_BIRD.rounds;
  const total = EARLY_BIRD.earlyFee * EARLY_BIRD.rounds;
  const wasTotalVat = Math.round(wasTotal * (1 + vat / 100));
  const totalVat = Math.round(total * (1 + vat / 100));
  return { wasTotal, total, wasTotalVat, totalVat, savingVat: wasTotalVat - totalVat };
}

export function EarlyBird({ tone = "dark", compact = false, fee, vatPct, photo = EARLY_BIRD_PHOTO, testid = "early-bird" }) {
  const vat = vatPct ?? EARLY_BIRD.vatPct;
  const f = earlyBirdFigures(fee ?? EARLY_BIRD.roundFee, vat);
  return (
    // Whole card is the link, like the landing discipline cards (client, 2026-09-17).
    <Link to={SEASON_ENTRY_PATH} className={`eb-card eb-card--${tone} ${compact ? "eb-card--compact" : ""}`} data-testid={testid} data-reveal="fade" aria-label="Enter the full season">
      <div className="eb-card__l">
        {photo && (
          <div className="eb-card__photo" data-testid={`${testid}-photo`}>
            {/* Eager at low priority (client, 2026-09-17: the photo was popping in
                after the card). Starts downloading with the page, never competes
                with the hero, is already there when the card scrolls up. */}
            <img src={photo} alt="" loading="eager" fetchPriority="low" decoding="async" />
          </div>
        )}
        {/* Client, 2026-09-19: "Early bird" is the heading; the six-rounds line moves top-right. */}
        <h3 className="eb-card__h" data-testid={`${testid}-heading`}>Early bird</h3>
        <p className="eb-card__p">
          Enter the season in one go and each round is AED {fmt(EARLY_BIRD.earlyFee)} instead of {fmt(fee ?? EARLY_BIRD.roundFee)}. One payment, one entry number for every round, per car.
        </p>
        <span className="btn btn--fill eb-card__cta" data-testid={`${testid}-cta`}>
          Enter the full season
        </span>
      </div>
      <div className="eb-card__r">
        {/* Right column (client, 2026-09-19): the line, the price + VAT, what you save.
            The struck-through "was" price and the totals are gone. */}
        <div className="eb-card__row eb-card__row--line">
          <span className="eb-card__line" data-testid={`${testid}-kicker`}>All six rounds. One entry.</span>
        </div>
        <div className="eb-card__row">
          <div className="eb-card__price" data-testid={`${testid}-price`}>
            <span className="eb-card__cur">AED</span>
            <span className="eb-card__num tech-num">{fmt(f.total)}</span>
            <span className="eb-card__vat">+{vat}% VAT</span>
          </div>
        </div>
        <div className="eb-card__row eb-card__row--diff">
          <span className="eb-card__k">You save</span>
          <span className="eb-card__diff tech-num" data-testid={`${testid}-diff`}>AED {fmt(f.savingVat)}</span>
        </div>
      </div>
    </Link>
  );
}
